<template>
  <div class="py-6 overflow-hidden">
    <div class="scrolling-wrapper">
      <div class="scrolling-content" ref="scrollContent">
        <div v-for="(icon, index) in duplicatedIcons" :key="index" class="icon-item">
          <div class="icon-container">
            <img :src="icon" :alt="`Partner logo ${index + 1}`" class="icon-image">
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, onMounted, computed, onBeforeUnmount } from 'vue'

export default defineComponent({
  name: 'ScrollingIcons',
  setup() {
    const scrollContent = ref(null)
    const animationId = ref(null)
    
    const icons = [
      '/media/icons_land/ChatGPT(1).png',
      '/media/icons_land/canva2.png',
      '/media/icons_land/google2.png',
      '/media/icons_land/iherb2.png',
      '/media/icons_land/mj-land.png',
      '/media/icons_land/steam-land.png',
      '/media/icons_land/youtube-land.png',
      '/media/icons_land/zoom2.png'
    ]
    
    // Create a larger set of duplicated icons for a seamless loop
    const duplicatedIcons = computed(() => [...icons, ...icons, ...icons, ...icons, ...icons])
    
    onMounted(() => {
      // Apply custom styling to specific icons
      customizeIconSizes()
      
      // Wait for the content to be fully rendered
      setTimeout(() => {
        setupInfiniteScroll()
      }, 100)
      
      // Add event listeners for pause on hover
      if (scrollContent.value) {
        scrollContent.value.addEventListener('mouseenter', pauseAnimation)
        scrollContent.value.addEventListener('mouseleave', startAnimation)
      }
    })
    
    onBeforeUnmount(() => {
      stopAnimation()
      
      // Remove event listeners
      if (scrollContent.value) {
        scrollContent.value.removeEventListener('mouseenter', pauseAnimation)
        scrollContent.value.removeEventListener('mouseleave', startAnimation)
      }
    })
    
    const customizeIconSizes = () => {
      if (!scrollContent.value) return
      
      // Get all icon images
      const iconImages = scrollContent.value.querySelectorAll('.icon-image')
      
      // Apply custom styling to specific icons based on their src
      iconImages.forEach(img => {
        const src = img.getAttribute('src')
        
        if (src.includes('ChatGPT')) {
          img.style.width = '90px'
        } else if (src.includes('steam')) {
          img.style.width = '85px'
        } else if (src.includes('youtube')) {
          img.style.width = '85px'
        } else if (src.includes('zoom')) {
          img.style.width = '80px'
        } else if (src.includes('mj-land')) {
          img.style.width = '90px'
        } else if (src.includes('canva')) {
          img.style.marginTop = '-5px' // Adjust vertical alignment
          img.style.width = '80px'
        }
      })
    }
    
    const setupInfiniteScroll = () => {
      if (!scrollContent.value) return
      
      // Calculate the width of a single set of icons
      const iconSetWidth = scrollContent.value.scrollWidth / 5
      
      // Start with position in the middle of the duplicated icons
      // This allows scrolling both forward and backward seamlessly
      let currentPosition = iconSetWidth * 2
      scrollContent.value.style.transform = `translateX(-${currentPosition}px)`
      
      startAnimation(currentPosition)
    }
    
    const startAnimation = (startPosition = null) => {
      if (!scrollContent.value) return
      
      let startTime = null
      const speed = 15 // pixels per second - even slower for better visibility
      
      // Safely get current position with null checks
      let currentPosition = startPosition !== null ? startPosition : 0
      if (startPosition === null && scrollContent.value.style.transform) {
        const transformValue = scrollContent.value.style.transform.match(/translateX\(-([0-9.]+)px\)/)
        if (transformValue && transformValue[1]) {
          currentPosition = parseFloat(transformValue[1])
        }
      }
      
      // Calculate the width of a single set of icons
      const iconSetWidth = scrollContent.value.scrollWidth / 5
      
      const animate = (timestamp) => {
        if (!startTime) startTime = timestamp
        const elapsed = timestamp - startTime
        
        // Check if scrollContent is still available
        if (!scrollContent.value) {
          cancelAnimationFrame(animationId.value)
          return
        }
        
        // Calculate how far to move based on elapsed time
        const pixelsToMove = (elapsed / 1000) * speed
        currentPosition += pixelsToMove
        
        // Check if we need to reset position for seamless looping
        // When we've scrolled past one complete set, jump back by one set width
        // This creates the illusion of infinite scrolling
        if (currentPosition >= iconSetWidth * 3) {
          currentPosition -= iconSetWidth
        }
        
        scrollContent.value.style.transform = `translateX(-${currentPosition}px)`
        startTime = timestamp
        
        animationId.value = requestAnimationFrame(animate)
      }
      
      animationId.value = requestAnimationFrame(animate)
    }
    
    const pauseAnimation = () => {
      stopAnimation()
    }
    
    const stopAnimation = () => {
      if (animationId.value) {
        cancelAnimationFrame(animationId.value)
        animationId.value = null
      }
    }
    
    return {
      scrollContent,
      duplicatedIcons
    }
  }
})
</script>

<style scoped>
.scrolling-wrapper {
  width: 100%;
  overflow: hidden;
}

.scrolling-content {
  display: flex;
  align-items: center;
  will-change: transform;
}

.icon-item {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 12px;
  margin: 0 12px;
}

.icon-container {
  width: 120px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-image {
  max-height: 30px;
  width: 70px;
  object-fit: contain;
  opacity: 0.7;
  transition: opacity 0.3s;
}

.icon-image:hover {
  opacity: 1;
}
</style>
